"use client"

import React, { useState } from "react"
import { X, ChevronDown, ChevronUp, Filter } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import { useWorkflow } from "@/context/workflow-context-simple"
import { useTranslation } from "@/hooks/use-translation"
import { useLanguage } from "@/context/language-context"
import { getLocalizedField } from "@/utils/language-utils"
import { useCodeValues } from "@/hooks/use-code-values"
import { getSearchColumnPath } from "@/utils/column-type-helpers"

interface FilterCategoryProps {
  title: string
  options: Array<{ label: string; value: string; count?: number }>
  selectedValues: string[]
  onChange: (values: string[]) => void
}

function FilterCategory({ title, options, selectedValues, onChange }: FilterCategoryProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const { t } = useTranslation()

  // Improved search that checks both label and value
  const filteredOptions = options.filter((option) => {
    const searchLower = searchTerm.toLowerCase()
    const matchesLabel = option.label.toLowerCase().includes(searchLower)
    const matchesValue = option.value.toLowerCase().includes(searchLower)
    const shouldInclude = matchesLabel || matchesValue
    
    return shouldInclude
  })


  // Add null/blank option to the options list only if it matches search or search is empty
  const blankOption = { label: t('blank'), value: "__BLANK__", count: undefined }
  const shouldIncludeBlank = !searchTerm || 
    blankOption.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    'blank'.includes(searchTerm.toLowerCase())
  
  const allOptions = [
    ...filteredOptions,
    ...(shouldIncludeBlank ? [blankOption] : [])
  ]

  // Sort options: selected items first, then unselected, each group sorted alphabetically  
  const sortedOptions = React.useMemo(() => {
    if (!allOptions || allOptions.length === 0) return []
    
    return [...allOptions].sort((a, b) => {
      // Convert both to strings for consistent comparison
      const aValue = String(a.value)
      const bValue = String(b.value)
      const aSelected = selectedValues.some(val => String(val) === aValue)
      const bSelected = selectedValues.some(val => String(val) === bValue)
      
      // If selection status is different, selected items come first
      if (aSelected !== bSelected) {
        return aSelected ? -1 : 1
      }
      
      // If both have same selection status, sort alphabetically by label
      return (a.label || '').localeCompare(b.label || '')
    })
  }, [allOptions, selectedValues])


  const toggleSelectAll = (checked: boolean) => {
    if (checked) {
      // Select all options including null/blank values
      const allValues = allOptions.map((option) => option.value)
      onChange(allValues)
    } else {
      onChange([])
    }
  }

  // Determine the state of the "Select All" checkbox
  const selectedValueStrings = selectedValues.map(v => String(v))
  const allOptionValueStrings = allOptions.map(opt => String(opt.value))
  const isAllSelected = allOptions.length > 0 && 
    allOptionValueStrings.every(val => selectedValueStrings.includes(val))
  const hasPartialSelection = selectedValues.length > 0 && !isAllSelected

  // Use indeterminate state when some but not all options are selected
  const selectAllState = isAllSelected ? true : hasPartialSelection ? 'indeterminate' : false

  return (
    <div className="border-b">
      <button
        className="w-full py-3 px-4 flex items-center justify-between text-left font-medium"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{title}</span>
        {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {isOpen && (
        <div className="px-4 pb-4">
          <div className="mb-2">
            <Input
              placeholder={`Select ${title.toLowerCase()}...`}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value)
              }}
              className="mb-2"
            />
          </div>

          <div className="max-h-[300px] overflow-y-auto border rounded-md">
            <div className="p-2 border-b">
              <div className="flex items-center">
                <Checkbox
                  id={`select-all-${title}`}
                  checked={selectAllState}
                  onCheckedChange={(checked) => toggleSelectAll(!!checked)}
                />
                <label htmlFor={`select-all-${title}`} className="ml-2 text-sm">
                  Select All
                </label>
              </div>
            </div>

            {/* Separator line after Select all */}
            <div className="w-full h-px bg-gray-200 mx-2" />

            <div className="p-2">
              {sortedOptions.length === 0 ? (
                <div className="text-sm text-gray-500 py-2">No results found</div>
              ) : (
                sortedOptions.map((option) => (
                  <div key={option.value} className="flex items-center py-1">
                    <Checkbox
                      id={`${title}-${option.value}`}
                      checked={selectedValues.some(val => String(val) === String(option.value))}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          onChange([...selectedValues, option.value])
                        } else {
                          onChange(selectedValues.filter((value) => String(value) !== String(option.value)))
                        }
                      }}
                    />
                    <label htmlFor={`${title}-${option.value}`} className="ml-2 text-sm">
                      {option.label} {option.count !== undefined && `(${option.count})`}
                    </label>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setIsOpen(false)
              }}
            >
              OK
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSearchTerm("")
                onChange([])
                setIsOpen(false)
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export function FilterDrawer() {
  const { t } = useTranslation()
  const { language } = useLanguage()
  const codeValues = useCodeValues()
  const {
    toggleFilterDrawer,
    filters,
    updateFilter,
    clearFilters,
    configuration,
    currentStage,
    globalFilters,
    setGlobalFilters,
    clearAllFilters,
    facets,
    workOrdersFacets,
    assignedUsers,
  } = useWorkflow()

  // Get global filter fields and options
  const getGlobalFilterFields = () => {
    if (!Array.isArray(configuration?.globalFilterIds) || configuration.globalFilterIds.length === 0) return []
    
    return configuration.globalFilterIds
      .map(fid => {
        const field = configuration.fieldsInUse.find(f => f.fieldConfigurationId === fid)
        if (!field) return undefined
        
        // Get facet options for this field
        const facetOptions = getFacetOptionsForField(field)
        if (!facetOptions || facetOptions.length === 0) return undefined
        
        // Map options with display label
        const options = facetOptions.map((opt: { value: string; count: number | undefined }) => {
          let label = getOptionLabel(opt, field)
          return { ...opt, label }
        })
        
        return { field, options }
      })
      .filter((x): x is { field: any; options: { label: string; value: string; count: number }[] } => !!x)
  }

  // Helper to get facet options for a field
  const getFacetOptionsForField = (field: any) => {
    if (!field) return []

    // If column has CodeValueSource, use code values from the hook instead of facets
    const hasCodeValueSource = field.codeValueSource && codeValues && codeValues.isReady
    if (hasCodeValueSource) {
      try {
        const codeValuesFromHook = codeValues.getCodeValues(field.codeValueSource, false)
        if (codeValuesFromHook && Array.isArray(codeValuesFromHook)) {
          // Convert code values to facet format
          return codeValuesFromHook
            .filter((item: any) => 
              item && 
              item.code && 
              item.code.trim() !== '' && 
              item.text && 
              item.text.trim() !== ''
            )
            .map((item: any) => ({
              value: item.code,
              count: undefined, // Don't show count for code value options
              code: item.code,
              titleEnglish: item.text,
              titleFrench: item.text,
              color: item.Icon || '',
              isDeleted: false
            }))
        }
      } catch (error) {
        console.warn('Failed to get code values from hook:', error)
      }
    }

    if (!workOrdersFacets) return []

    // Get the search path for facet lookup
    const facetKey = getSearchColumnPath(field);
    
    // Try to find facet data with search path
    if (Array.isArray(workOrdersFacets[facetKey]) && workOrdersFacets[facetKey].length > 0) {
      return workOrdersFacets[facetKey].filter((opt: { value: string; count: number }) => opt.value !== "")
    }
    
    // Fallback to direct columnCode match
    if (Array.isArray(workOrdersFacets[field.columnCode]) && workOrdersFacets[field.columnCode].length > 0) {
      return workOrdersFacets[field.columnCode].filter((opt: { value: string; count: number }) => opt.value !== "")
    }
    
    // Try case-insensitive match
    const foundKey = Object.keys(workOrdersFacets).find(
      k => k.toLowerCase() === (field.columnCode || '').toLowerCase()
    )
    if (foundKey && Array.isArray(workOrdersFacets[foundKey])) {
      return workOrdersFacets[foundKey].filter((opt: { value: string; count: number }) => opt.value !== "")
    }
    
    // Try replacing / with nothing (for edge cases)
    const altKey = (field.columnCode || '').replace(/\//g, '')
    const foundAlt = Object.keys(workOrdersFacets).find(
      k => k.replace(/\//g, '').toLowerCase() === altKey.toLowerCase()
    )
    if (foundAlt && Array.isArray(workOrdersFacets[foundAlt])) {
      return workOrdersFacets[foundAlt].filter((opt: { value: string; count: number }) => opt.value !== "")
    }
    
    return []
  }

  // Helper to get option label - improved version matching workflow-grid.tsx logic
  const getOptionLabel = (opt: any, field: any) => {
    const value = opt.value
    
    // For assigned users, try to get the full name and format it properly
    if ((field.columnCode === 'AssignedToUser' || field.columnCode === 'AssignedToUser/Email') && Array.isArray(assignedUsers)) {
      const user = assignedUsers.find(u => u.email?.toLowerCase() === value?.toLowerCase())
      if (user && user.fullName) {
        // Format full name with proper capitalization
        return user.fullName.replace(/\w\S*/g, (txt: string) => 
          txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase()
        )
      } else if (user) {
        return user.email
      }
    }
    
    // For boolean fields, make the label more readable with localized values
    if (field.columnTypeCode?.toLowerCase() === 'boolean') {
      if (value === 'true') return t('yes') || 'Yes'
      if (value === 'false') return t('no') || 'No'
      return value
    }
    
    // For custom value options, use localized titles
    if (opt.titleEnglish || opt.titleFrench) {
      return language === 'fr' ? (opt.titleFrench || opt.titleEnglish || opt.code || value) : (opt.titleEnglish || opt.code || value)
    }
    
    // For fields with codeValueSource, try to get display value from code values
    if (field.codeValueSource && codeValues && codeValues.isReady && value) {
      try {
        const displayText = codeValues.getCodeValueTextByCode(String(value), field.codeValueSource)
        if (displayText) {
          return displayText
        }
      } catch (error) {
        console.warn('Failed to get code value text for display:', error)
      }
    }
    
    // Return the existing label if available, otherwise the value
    return opt.label || value
  }

  // Get current selections for each field
  const getCurrentSelections = (fieldId: string) => {
    const globalFilter = globalFilters.find(f => f.fieldConfigurationId === fieldId)
    
    if (!globalFilter || !globalFilter.values) return []
    
    try {
      const metadata = JSON.parse(globalFilter.values)
      
      if (metadata.isSelectAll) {
        // When "Select All" is active, return all available option values
        // so that individual checkboxes show as checked
        const field = configuration?.fieldsInUse.find(f => f.fieldConfigurationId === fieldId)
        if (field) {
          const facetOptions = getFacetOptionsForField(field)
          const allOptionValues = [
            ...facetOptions.map((opt: { value: string }) => opt.value),
            '__BLANK__'
          ]
          return allOptionValues
        }
        return ['__SELECT_ALL__']
      }
      
      const selections = []
      if (metadata.hasBlanks) selections.push('__BLANK__')
      if (metadata.regularValues) selections.push(...metadata.regularValues)
      return selections
    } catch (error) {
      console.error(`Error parsing metadata for ${fieldId}:`, error)
      return []
    }
  }

  // Handle filter changes
  const handleFilterChange = (fieldId: string, values: string[]) => {
    
    const field = configuration?.fieldsInUse.find(f => f.fieldConfigurationId === fieldId)
    if (!field) return

    // Check if this is a "Select All" operation
    const facetOptions = getFacetOptionsForField(field)
    const allOptionValues = [
      ...facetOptions.map((opt: { value: string }) => opt.value),
      '__BLANK__'
    ]
    const isSelectAll = values.length === allOptionValues.length && 
                       allOptionValues.every(val => values.includes(val))

    // If "Select All" is selected, store the metadata but don't include in search request
    if (isSelectAll) {
      // Store the "Select All" state in metadata
      const metadata = {
        isSelectAll: true,
        hasBlanks: true,
        regularValues: facetOptions.map((opt: { value: string }) => opt.value)
      }
      
      // Update global filters with metadata but no filter string
      const newGlobalFilters = globalFilters.filter(f => f.fieldConfigurationId !== fieldId)
      newGlobalFilters.push({
        fieldConfigurationId: fieldId,
        filter: '', // No filter string when "Select All" is active
        values: JSON.stringify(metadata)
      })
      setGlobalFilters(newGlobalFilters)
      return
    }

    // Build filter string for regular selections
    let filterString = ''
    const hasBlank = values.includes('__BLANK__')
    const regularValues = values.filter(v => v !== '__BLANK__')
    
    if (field.columnTypeCode?.toLowerCase() === 'textlist') {
      // Special handling for TextList with AllowMultipleSelections
      const orConditions = regularValues
        .map((val: string) => `x eq '${val.replace(/'/g, "''")}'`)
        .join(' or ')
      if (hasBlank && regularValues.length > 0) {
        // Include empty collections or those matching selected values
        filterString = `(not ${field.columnCode}/any() or ${field.columnCode}/any(x: ${orConditions}))`
      } else if (hasBlank) {
        // Only empty collections
        filterString = `not ${field.columnCode}/any()`
      } else if (regularValues.length > 0) {
        // Only selected values
        filterString = `${field.columnCode}/any(x: ${orConditions})`
      }
    } else if (hasBlank && regularValues.length > 0) {
      // (blank) combined with regular values
      const blankCondition = `${field.columnCode} eq null or ${field.columnCode} eq ''`
      const regularConditions = regularValues.map(val => `${field.columnCode} eq '${val.replace(/'/g, "''")}'`).join(' or ')
      filterString = `(${blankCondition}) or (${regularConditions})`
    } else if (hasBlank) {
      // Only (blank): null or empty values
      filterString = `${field.columnCode} eq null or ${field.columnCode} eq ''`
    } else if (regularValues.length > 0) {
      // Regular values only
      if (field.columnTypeCode?.toLowerCase() === 'boolean') {
        const orConditions = regularValues.map(val => `${field.columnCode} eq ${val.toLowerCase()}`).join(' or ')
        filterString = regularValues.length > 1 ? `(${orConditions})` : orConditions
      } else {
        const orConditions = regularValues.map(val => `${field.columnCode} eq '${val.replace(/'/g, "''")}'`).join(' or ')
        filterString = regularValues.length > 1 ? `(${orConditions})` : orConditions
      }
    }

    // Filter string is already properly formatted - no need to add Stage/IsDeleted prefix for global filters

    // Create metadata for reapplication
    const metadata = {
      isSelectAll: false,
      hasBlanks: hasBlank,
      regularValues: regularValues
    }

    // Update global filters
    const newGlobalFilters = globalFilters.filter(f => f.fieldConfigurationId !== fieldId)
    if (filterString) {
      newGlobalFilters.push({
        fieldConfigurationId: fieldId,
        filter: filterString,
        values: JSON.stringify(metadata)
      })
    }
    setGlobalFilters(newGlobalFilters)
  }

  const globalFilterFields = getGlobalFilterFields()

  return (
    <div className="fixed inset-y-0 right-0 w-[320px] bg-white shadow-lg border-l z-50 flex flex-col">
      <div className="border-b p-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter size={16} />
          <h3 className="font-medium">Filters</h3>
        </div>
        <Button variant="ghost" size="icon" onClick={toggleFilterDrawer}>
          <X size={18} />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {globalFilterFields.map(({ field, options }) => (
          <FilterCategory
            key={field.fieldConfigurationId}
            title={getLocalizedField(field, 'columnName', language) || field.columnCode}
            options={options}
            selectedValues={getCurrentSelections(field.fieldConfigurationId)}
            onChange={(values) => handleFilterChange(field.fieldConfigurationId, values)}
          />
        ))}
      </div>

      <div className="border-t p-3 flex justify-between bg-white sticky bottom-0">
        <Button variant="outline" onClick={clearAllFilters}>
          Clear All
        </Button>
        <Button onClick={toggleFilterDrawer} className="bg-blue-600 hover:bg-blue-700">
          Apply
        </Button>
      </div>
    </div>
  )
}
